import React from "react";
import { Modal, ModalBody, ModalHeader } from "shared/components/modal";
import Size from "shared/enums/Size";
import { useAdditionalChargeService } from "./service";
import orderBy from "lodash/orderBy";
import PurchaseOrderCard from "apps/myExperience/components/purchaseHistory/components/purchaseOrderCard";
import PaymentComponentWrapper from "shared/components/paymentWrapper";
import { useAuth } from "shared/context/auth";
import { AdditionalChargeInterface } from "./types";
import { useCopies } from "shared/hooks/useCopies";
import Button from "shared/components/button";
import { getFormattedCurrency } from "shared/utils";
import { useEntity } from "apps/console/context/entity";
import { useRegDetail } from "shared/context/regDetail";
import {
  getStripeReturnUrlForMyExp,
  isInvoicePaymentRequestedAndPending,
  isPaymentReceiptPendingForPO
} from "apps/myExperience/utils";
import NoDataFound from "shared/components/noDataFound";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export default function AdditionalCharge() {
  const auth = useAuth();
  useLogAnalyticsScreenHook({ properties: { screenType: AnalyticsScreenNameEnum.ADDITIONAL_CHARGE } });
  const id = auth.userDetail?.id as string;
  const service = useAdditionalChargeService(id);
  const copies = useCopies();
  return (
    <React.Fragment>
      <Modal size={Size.LARGE}>
        <ModalHeader left={copies.get("additional.charge")} onClose={service.modalCloseHandler} />
        <ModalBody>
          {service.charges.length === 0 ? (
            <NoDataFound label={copies.get("no.additional.charges.found")} height="30vh" />
          ) : (
            orderBy(service.charges, "createdDate.dateTime", "desc").map((purchaseOrder, index) => (
              <PurchaseOrderCard
                key={index}
                purchaseOrder={purchaseOrder}
                makePayment={
                  <Button
                    label={copies.get(
                      isInvoicePaymentRequestedAndPending(purchaseOrder)
                        ? "invoice.payment.request.update.or.pay.btn.label"
                        : isPaymentReceiptPendingForPO(purchaseOrder)
                        ? "offline.payment.request.update.or.pay.btn.label"
                        : "make.payment"
                    )}
                    onClick={() => {
                      service.setSelectedPurchaseOrder(purchaseOrder);
                    }}
                  />
                }
              />
            ))
          )}
          {service.selectedPurchaseOrder && <PaymentModal service={service} id={id} />}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
}

function PaymentModal({ service, id }: { service: AdditionalChargeInterface; id: string }) {
  const copies = useCopies();
  const { showCardIcons, invoicePaymentEnabled } = useRegDetail();
  const { localeId } = useEntity();

  return (
    <Modal size={Size.MEDIUM} className="additionalChargeModal">
      <ModalHeader left={copies.get("make.payment")} onClose={() => service.setSelectedPurchaseOrder(null)} />
      <ModalBody>
        {service.selectedPurchaseOrder && (
          <div className="makePaymentWrapper">
            <div className="makePaymentHeader">
              <p className="makePaymentHeader--label">{copies.get("amount.payable")}</p>
              <span className="makePaymentHeader--amount">
                {getFormattedCurrency(service.selectedPurchaseOrder.amount, localeId)}
              </span>
            </div>
            <PaymentComponentWrapper
              successHandler={service.handlePaymentSuccess}
              amount={service.selectedPurchaseOrder.amount}
              registrationParticipantId={id}
              purchaseOrderId={service.selectedPurchaseOrder.purchaseOrderId}
              showCardIcons={showCardIcons}
              onPurchaseOrderVerificationFailure={service.onPurchaseOrderVerificationFailure}
              errorHandler={service.onPaymentErrorHandler}
              invoicePaymentEnabled={invoicePaymentEnabled}
              invoicePaymentConfig={{
                initialEmail: service.selectedPurchaseOrder.invoicePaymentRequest?.invoicePayerEmail
              }}
              invoicePayerId={service.selectedPurchaseOrder.invoicePaymentRequest?.invoicePayerId}
              invoicePaymentRequestId={service.selectedPurchaseOrder.invoicePaymentRequest?.invoicePaymentRequestId}
              invoicePayerPaymentRequestSuccessHandler={service.invoicePayerPaymentRequestSuccessHandler}
              resendInvoicePaymentReminderSuccessHandler={service.reminderSendSuccessHandler}
              stripeReturnUrl={getStripeReturnUrlForMyExp({
                registrationParticipantId: id,
                purchaseOrderId: service.selectedPurchaseOrder.purchaseOrderId
              })}
              offlinePaymentConfig={{
                offlinePaymentRequested: service.selectedPurchaseOrder.hasRequestedOfflinePayment,
                paymentReferenceId: service.selectedPurchaseOrder.transaction?.providerPaymentId
              }}
              offlineTransactionUpdateSuccessHandler={service.offlineTransactionUpdateSuccessHandler}
              offlineTransactionCancelSuccessHandler={service.offlineTransactionCancelSuccessHandler}
            />
          </div>
        )}
      </ModalBody>
    </Modal>
  );
}
